/**
 * ScheduleSCurveChart — the canonical Plan vs Actual vs Forecast S-curve.
 *
 * One component serves the Schedule module and the project dashboard so the
 * two pages can never render different curves. Owns the day/week/month x-axis
 * scale and refetches on change.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Area, CartesianGrid, ComposedChart, Legend, Line, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Loader2, TrendingUp, Layers } from 'lucide-react';
import api from '@/lib/api';
import { usePanelTheme } from '@/components/project/panelTheme';
import ModuleDrilldownDrawer from '@/components/project/ModuleDrilldownDrawer';

const SCALES = [
  { key: 'day', label: 'Daily' },
  { key: 'week', label: 'Weekly' },
  { key: 'month', label: 'Monthly' },
];

export const ScheduleSCurveChart = ({
  url,                       // endpoint that accepts ?interval=day|week|month
  title = 'S-Curve Progress (Plan vs Actual vs Forecast)',
  height = 320,
  defaultInterval = 'week',
  compact = false,
  onSummary,                 // optional: bubble the summary up to the page
  testIdPrefix = 'scurve',
  projectId,                 // enables the L2 · L3 drilldown button
}) => {
  const T = usePanelTheme();
  const [interval, setInterval] = useState(defaultInterval);
  const [drill, setDrill] = useState(false);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (scale) => {
    if (!url) return;
    setLoading(true); setError(null);
    try {
      const r = await api.get(`${url}${url.includes('?') ? '&' : '?'}interval=${scale}`);
      setPayload(r.data);
      onSummary?.(r.data?.summary || null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load S-curve');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  useEffect(() => { load(interval); }, [load, interval]);

  const points = useMemo(() => (payload?.s_curve_data || []).map((p) => ({
    ...p,
    plan_cumulative: p.plan ?? 0,
    actual_cumulative: p.actual ?? 0,
    forecast_cumulative: p.forecast ?? 0,
  })), [payload]);

  // Match the data date to the nearest plotted point — period labels repeat
  // across years so the ISO date is the only safe key for the reference line.
  const dataDateKey = useMemo(() => {
    const dd = payload?.summary?.status_date;
    if (!dd || !points.length) return null;
    const ts = new Date(dd).getTime();
    if (Number.isNaN(ts)) return null;
    let best = null, delta = Infinity;
    points.forEach((p) => {
      const t = new Date(p.date).getTime();
      const d = Math.abs(t - ts);
      if (d < delta) { delta = d; best = p.date; }
    });
    return best;
  }, [payload, points]);

  const tickFmt = (d) => {
    if (!d) return '';
    const pt = points.find((p) => p.date === d);
    if (pt?.period_label) return pt.period_label;
    try {
      const dt = new Date(d);
      return interval === 'month'
        ? dt.toLocaleString('en-US', { month: 'short', year: '2-digit' })
        : dt.toLocaleString('en-US', { month: 'short', day: '2-digit' });
    } catch { return d; }
  };

  return (
    <div className="rounded-2xl p-5" data-testid={`${testIdPrefix}-card`}
         style={{ background: T.card, border: `1px solid ${T.border}`,
                  boxShadow: T.isDark ? 'none' : '0 1px 2px rgba(15,23,42,0.04)' }}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <TrendingUp className="w-4 h-4 text-[#1E4E8C] shrink-0" />
          <h4 className="font-semibold text-sm truncate" style={{ color: T.text }}>{title}</h4>
          {payload?.scheduleName && (
            <span className="text-[10px] font-mono truncate" style={{ color: T.muted }}>{payload.scheduleName}</span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0" data-testid={`${testIdPrefix}-scale-toggle`}>
          {projectId && (
            <button type="button" onClick={() => setDrill(true)}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold mr-1"
              style={{ background: `${T.accent}18`, color: T.accent, border: `1px solid ${T.accent}44` }}
              data-testid={`${testIdPrefix}-drilldown-btn`}>
              <Layers className="w-3 h-3" /> L2 · L3
            </button>
          )}
          {SCALES.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setInterval(s.key)}
              className="text-[10px] px-2 py-0.5 rounded-full font-medium transition-colors"
              style={{
                background: interval === s.key ? T.accent : 'transparent',
                border: `1px solid ${interval === s.key ? T.accent : T.border}`,
                color: interval === s.key ? '#fff' : T.muted,
              }}
              data-testid={`${testIdPrefix}-scale-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {!compact && (
        <div className="text-[10px] mb-2" style={{ color: T.muted }}>
          <span className="inline-flex items-center gap-1 mr-4"><span className="w-3 h-0.5 bg-[#1E4E8C] inline-block" /> Plan</span>
          <span className="inline-flex items-center gap-1 mr-4"><span className="w-3 h-0.5 bg-[#0E7C6B] inline-block" /> Actual</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-0.5 bg-orange-500 inline-block" style={{ borderTop: '2px dashed' }} /> Forecast (dashed)</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center gap-2 text-xs" style={{ height, color: T.muted }}
             data-testid={`${testIdPrefix}-loading`}>
          <Loader2 className="w-4 h-4 animate-spin" /> Building {SCALES.find(s => s.key === interval)?.label.toLowerCase()} curve…
        </div>
      ) : error ? (
        <div className="flex items-center justify-center text-xs" style={{ height, color: T.bad }}
             data-testid={`${testIdPrefix}-error`}>{error}</div>
      ) : points.length === 0 ? (
        <div className="flex items-center justify-center text-xs text-center px-6" style={{ height, color: T.muted }}
             data-testid={`${testIdPrefix}-empty`}>
          {payload?.message || 'No schedule data available for this project.'}
        </div>
      ) : (
        <>
          <div style={{ height }} data-testid={`${testIdPrefix}-chart`}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={points} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="scPlanGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1E4E8C" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#1E4E8C" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="scActualGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0E7C6B" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#0E7C6B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={T.grid} />
                <XAxis
                  dataKey="date"
                  stroke={T.muted}
                  fontSize={10}
                  angle={-45}
                  textAnchor="end"
                  height={56}
                  interval="preserveStartEnd"
                  minTickGap={interval === 'day' ? 24 : 8}
                  tickFormatter={tickFmt}
                />
                <YAxis stroke={T.muted} fontSize={11} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <Tooltip
                  contentStyle={{ background: T.card, border: `1px solid ${T.border}`, fontSize: 11, color: T.text }}
                  labelFormatter={tickFmt}
                  formatter={(value, name) => [`${Number(value || 0).toFixed(1)}%`, name]}
                />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
                <Area type="monotone" dataKey="plan_cumulative"   name="Plan %"     stroke="#1E4E8C" fill="url(#scPlanGrad)"   strokeWidth={2} />
                <Area type="monotone" dataKey="actual_cumulative" name="Actual %"   stroke="#0E7C6B" fill="url(#scActualGrad)" strokeWidth={2} />
                <Line type="monotone" dataKey="forecast_cumulative" name="Forecast %" stroke="#F97316" strokeDasharray="5 5" strokeWidth={2} dot={false} />
                {dataDateKey && (
                  <ReferenceLine
                    x={dataDateKey}
                    stroke="#ef4444"
                    strokeWidth={2.5}
                    strokeDasharray="6 4"
                    ifOverflow="extendDomain"
                    label={{
                      value: `Data Date · ${payload?.summary?.status_date}`,
                      position: 'insideTopRight', fill: '#ef4444', fontSize: 10, fontWeight: 600,
                    }}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          {!compact && (
            <div className="mt-3 text-[10px]" style={{ color: T.muted }}>
              Acceptance Criteria: Plan reaches 100% at project finish • Actual stops at Data Date • Forecast continues to 100%
              {payload?.pointCount ? ` · ${payload.pointCount} ${interval}ly points` : ''}
            </div>
          )}
        </>
      )}
      {drill && (
        <ModuleDrilldownDrawer projectId={projectId} module="schedule" onClose={() => setDrill(false)} />
      )}
    </div>
  );
};

export default ScheduleSCurveChart;
